import React, { useEffect, useState, useMemo } from 'react'
import { observer } from 'mobx-react'
import {
    PeriodDropdownContainer,
    PeriodDropdownHeader,
    PeriodDropdownBody,
    PeriodYearOption,
    PeriodQuarterOption,
    PeriodCheckbox,
    PeriodLabel,
    PeriodOptionContent,
    PeriodExpandIcon,
    PeriodSeparator
} from './PeriodDropdown.styles'
import { CheckboxCheckmarkIcon, CheckmarkPartialIcon, ArrowRightIcon } from './icons'
import { DropdownSearch } from './DropdownSearch'
import { createFuzzySearch } from '../../../../../common/utils/fuzzy-search.utils'
import { useStore } from '../../../../app/data/root.store'
import { LogoLoader } from '../../../../../common/components/LogoLoader/LogoLoader'

export const PeriodDropdown = observer(({ selectedPeriods: propSelectedPeriods = [], onPeriodSelect }) => {
    const { vaultStore } = useStore()
    const [search, setSearch] = useState('')
    const [expandedYears, setExpandedYears] = useState(new Set(['2025']))
    const [selectedPeriods, setSelectedPeriods] = useState(new Set())

    const availablePeriods = useMemo(() => 
        vaultStore.userPeriods?.length > 0 ? vaultStore.userPeriods.filter(period => period.label && period.label.trim()) : []
    , [vaultStore.userPeriods])

    useEffect(() => {
        if (propSelectedPeriods && propSelectedPeriods.length > 0) {
            setSelectedPeriods(new Set(propSelectedPeriods.map(p => p.key)))
        } else {
            setSelectedPeriods(new Set())
        }
    }, [propSelectedPeriods])

    const getQuartersForYear = (yearKey) => {
        const year = availablePeriods.find(y => y.key === yearKey)
        return year ? year.quarters?.map(q => q.key) : []
    }

    const areAllQuartersSelected = (yearKey) => {
        const year = availablePeriods.find(y => y.key === yearKey)
        if (year && year.type === 'no-period-files') {
            return selectedPeriods.has(yearKey)
        }

        const quarters = getQuartersForYear(yearKey)

        if (!quarters || quarters.length === 0) {
            return selectedPeriods.has(yearKey)
        }

        if (isFlattenedMode) {
            return selectedPeriods.has(yearKey)
        }

        return quarters.every(qKey => selectedPeriods.has(qKey)) && selectedPeriods.has(yearKey)
    }

    const areAnyQuartersSelected = (yearKey) => {
        const year = availablePeriods.find(y => y.key === yearKey)
        if (year && year.type === 'no-period-files') {
            return selectedPeriods.has(yearKey)
        }
        if (isFlattenedMode) {
            return false
        }
        const quarters = getQuartersForYear(yearKey)
        return quarters?.length > 0 && quarters.some(qKey => selectedPeriods.has(qKey))
    }

    const toggleYear = (yearKey) => {
        const newExpanded = new Set(expandedYears)
        if (newExpanded.has(yearKey)) {
            newExpanded.delete(yearKey)
        } else {
            newExpanded.add(yearKey)
        }
        setExpandedYears(newExpanded)
    }

    const handleYearSelection = (yearKey) => {
        const newSelected = new Set(selectedPeriods)
        const year = availablePeriods.find(y => y.key === yearKey)

        if (year && year.type === 'no-period-files') {
            if (newSelected.has(yearKey)) {
                newSelected.delete(yearKey)
            } else {
                newSelected.add(yearKey)
            }
            setSelectedPeriods(newSelected)
            onPeriodSelect(year)
            return
        }

        const quarters = getQuartersForYear(yearKey)

        if (!quarters || quarters.length === 0) {
            if (newSelected.has(yearKey)) {
                newSelected.delete(yearKey)
            } else {
                newSelected.add(yearKey)
            }
            setSelectedPeriods(newSelected)
            onPeriodSelect(year)
            return
        }

        if (isFlattenedMode) {
            if (newSelected.has(yearKey)) {
                newSelected.delete(yearKey)
            } else {
                newSelected.add(yearKey)
            }
            setSelectedPeriods(newSelected)
            onPeriodSelect(year)
            return
        }

        if (areAllQuartersSelected(yearKey)) {
            quarters.forEach(qKey => newSelected.delete(qKey))
            newSelected.delete(yearKey)
        } else {
            const unselectedQuarters = quarters.filter(qKey => !selectedPeriods.has(qKey))
            unselectedQuarters.forEach(qKey => newSelected.add(qKey))
            newSelected.add(yearKey)
        }

        setSelectedPeriods(newSelected)

        const wasYearSelected = selectedPeriods.has(yearKey)
        const isYearNowSelected = newSelected.has(yearKey)
        if (wasYearSelected !== isYearNowSelected) {
            onPeriodSelect(year)
        }

        quarters.forEach(qKey => {
            const quarter = year.quarters?.find(q => q.key === qKey)
            if (quarter) {
                const wasSelected = selectedPeriods.has(qKey)
                const isNowSelected = newSelected.has(qKey)

                if (wasSelected !== isNowSelected) {
                    onPeriodSelect(quarter)
                }
            }
        })
    }

    const handleQuarterSelection = (quarterKey) => {
        const newSelected = new Set(selectedPeriods)
        const quarter = availablePeriods.flatMap(y => y.quarters).find(q => q.key === quarterKey)

        if (newSelected.has(quarterKey)) {
            newSelected.delete(quarterKey)
        } else {
            newSelected.add(quarterKey)
        }

        setSelectedPeriods(newSelected)

        if (quarter) {
            onPeriodSelect(quarter)
        }
    }

    const filteredData = useMemo(() => {
        if (!search || !availablePeriods.length) return availablePeriods

        const yearFuzzySearch = createFuzzySearch(availablePeriods, ['label'], { threshold: 0.3 })
        const matchingYears = yearFuzzySearch(search)

        const allQuarters = availablePeriods.flatMap(year => 
            (year.quarters || []).map(quarter => ({ ...quarter, parentYear: year }))
        )
        
        const quarterFuzzySearch = createFuzzySearch(allQuarters, ['label'], { threshold: 0.3 })
        const matchingQuarters = quarterFuzzySearch(search)

        const yearKeys = new Set(matchingYears.map(y => y.key))
        const quarterParentKeys = new Set(matchingQuarters.map(q => q.parentYear.key))
        
        return availablePeriods.map(year => {
            const yearMatches = yearKeys.has(year.key)
            const hasMatchingQuarters = quarterParentKeys.has(year.key)
            
            if (yearMatches || hasMatchingQuarters) {
                const quarters = year.quarters || []
                const relevantQuarters = hasMatchingQuarters && !yearMatches 
                    ? matchingQuarters
                        .filter(q => q.parentYear.key === year.key)
                        .map(q => ({ key: q.key, label: q.label }))
                    : quarters
                    
                return {
                    ...year,
                    quarters: relevantQuarters
                }
            }
            return null
        }).filter(Boolean)
    }, [availablePeriods, search])

    const totalUniquePeriods = filteredData.reduce((count, year) => {
        const yearCount = year.quarters && year.quarters.length > 0 ? year.quarters.length : 1
        return count + yearCount
    }, 0)

    const isCollapsedMode = totalUniquePeriods > vaultStore.periodsExpandedThreshold
    const isFlattenedMode = !isCollapsedMode

    if (vaultStore.isLoadingPeriods) {
        return (
            <PeriodDropdownContainer>
                <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
                    <LogoLoader />
                </div>
            </PeriodDropdownContainer>
        )
    }


    
    return (
        <PeriodDropdownContainer onClick={(e) => e.stopPropagation()}>
            <PeriodDropdownHeader>
                <DropdownSearch
                    placeholder="Search period"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </PeriodDropdownHeader>

            <PeriodDropdownBody id="periods-dropdown-body">
                {filteredData.map((year, yearIndex) => (
                    <div key={year.key}>
                        {!(isFlattenedMode && year.quarters?.length > 0 && year.type === 'year' && !year.hasDirectFiles) && (
                            <PeriodYearOption onClick={() => handleYearSelection(year.key)}>
                                <PeriodOptionContent>
                                    <PeriodCheckbox>
                                        <input
                                            type="checkbox"
                                            checked={areAllQuartersSelected(year.key)}
                                            onChange={() => handleYearSelection(year.key)}
                                        />
                                        <div className={`checkmark ${!areAllQuartersSelected(year.key) && areAnyQuartersSelected(year.key) ? 'partial' : ''}`}>
                                            {areAllQuartersSelected(year.key) && <CheckboxCheckmarkIcon />}
                                            {!areAllQuartersSelected(year.key) && areAnyQuartersSelected(year.key) && <CheckmarkPartialIcon />}
                                        </div>
                                    </PeriodCheckbox>
                                    <PeriodLabel>{year.label}</PeriodLabel>
                                </PeriodOptionContent>
                                {year.quarters?.length > 0 && isCollapsedMode && year.type !== 'no-period-files' && (
                                    <PeriodExpandIcon
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            toggleYear(year.key)
                                        }}
                                        style={{
                                            transform: expandedYears.has(year.key) ? '' : 'rotate(180deg)',
                                            transition: 'transform 0.3s ease'
                                        }}
                                    >
                                        <ArrowRightIcon />
                                    </PeriodExpandIcon>
                                )}
                            </PeriodYearOption>
                        )}

                        {(expandedYears.has(year.key) || isFlattenedMode) && year.quarters?.map(quarter => (
                            <PeriodQuarterOption
                                key={quarter.key}
                                onClick={() => handleQuarterSelection(quarter.key)}
                                isIndented={isCollapsedMode}
                            >
                                <PeriodCheckbox>
                                    <input
                                        type="checkbox"
                                        checked={selectedPeriods.has(quarter.key)}
                                        onChange={() => handleQuarterSelection(quarter.key)}
                                    />
                                    <div className="checkmark">
                                        {selectedPeriods.has(quarter.key) && <CheckboxCheckmarkIcon />}
                                    </div>
                                </PeriodCheckbox>
                                <PeriodLabel>{quarter.label}</PeriodLabel>
                            </PeriodQuarterOption>
                        ))}

                        {(yearIndex < filteredData.length - 1) && (
                            <PeriodSeparator className={search ? 'search-active' : ''} />
                        )}
                    </div>
                ))}
            </PeriodDropdownBody>
        </PeriodDropdownContainer>
    )
})